/**
 * 표 폭 자동 정규화 — ProseMirror Plugin (appendTransaction)
 *
 *  JSON 단계 (contractExtract) 의 normalization 을 거치지 않은 표 (옛날 docJson, paste, manual edit)
 *  도 에디터 단에서 항상 페이지 본문 폭(644px) 안에 들어가도록 강제 재정규화.
 *
 *  ⚠ TipTap tableCell.attrs.colwidth 구조:
 *    - colspan=1 셀: colwidth = [W]     (한 컬럼)
 *    - colspan=N 셀: colwidth = [W1,W2,...WN]  (N 개 컬럼 폭 배열)
 *  → 한 행의 모든 cell 의 colwidth 배열을 평탄화해서 sum 하면 = 표 전체 폭
 *
 *  동작:
 *  - 매 transaction 마다 doc 트리를 walk 해서 table 노드 발견
 *  - 각 table 행의 모든 cell colwidth 평탄화 합 계산
 *  - 합이 PAGE_INNER_PX(644) 와 ±2px 이상 차이면 비례 스케일
 *  - 각 cell 에 다시 N개 (colspan 만큼) 의 정규화된 width 배열 셋업
 *  - 무한 재귀 방지 (자기 자신의 tx 면 skip)
 */
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { Node as PMNode } from "@tiptap/pm/model";

export const tableNormalizePluginKey = new PluginKey("contract-table-normalize");

const PAGE_INNER_PX = 644;
const MIN_CELL_PX = 24;

/** widths 합이 정확히 PAGE_INNER_PX 가 되도록 비례 축소 + 잔차 보정. floor 사용으로 절대 초과 X. */
const fitWidthsToPage = (widths: number[]): number[] => {
  if (!widths.length) return widths;
  const sum = widths.reduce((a, b) => a + b, 0);
  if (sum <= 0) return widths;
  const scale = PAGE_INNER_PX / sum;
  let scaled = widths.map((w) => Math.max(MIN_CELL_PX, Math.floor(w * scale)));
  let curSum = scaled.reduce((a, b) => a + b, 0);
  // MIN_CELL_PX 클램프로 합 초과시 가장 큰 셀부터 줄임
  while (curSum > PAGE_INNER_PX) {
    const maxIdx = scaled.indexOf(Math.max(...scaled));
    if (scaled[maxIdx] <= MIN_CELL_PX) break;
    const reduce = Math.min(curSum - PAGE_INNER_PX, scaled[maxIdx] - MIN_CELL_PX);
    scaled[maxIdx] -= reduce;
    curSum -= reduce;
  }
  // 합 부족시 가장 큰 셀에 잔차 추가
  if (curSum < PAGE_INNER_PX) {
    const maxIdx = scaled.indexOf(Math.max(...scaled));
    scaled[maxIdx] += PAGE_INNER_PX - curSum;
  }
  return scaled;
};

const arraysEqual = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
};

/** 한 table 노드의 cell 들 중 colwidth 재정규화가 필요한 것 수집 */
const collectCellUpdates = (
  table: PMNode,
  tablePos: number
): Array<{ pos: number; newColwidth: number[] }> => {
  const updates: Array<{ pos: number; newColwidth: number[] }> = [];

  let rowPos = tablePos + 1; // table 시작 후 첫 row 의 시작 위치
  table.forEach((row) => {
    if (row.type.name !== "tableRow") {
      rowPos += row.nodeSize;
      return;
    }

    // 행의 모든 cell + 그 cell 의 colwidth 배열 모으기
    const cells: { pos: number; node: PMNode; widths: number[]; colspan: number }[] = [];
    let cellPos = rowPos + 1; // row 시작 후 첫 cell 의 시작 위치
    row.forEach((cell) => {
      if (cell.type.name === "tableCell" || cell.type.name === "tableHeader") {
        const cw = cell.attrs?.colwidth;
        const colspan = typeof cell.attrs?.colspan === "number" ? cell.attrs.colspan : 1;
        const widths: number[] = Array.isArray(cw)
          ? cw.map((n: any) => (typeof n === "number" && n > 0 ? n : 0))
          : [];
        cells.push({ pos: cellPos, node: cell, widths, colspan });
      }
      cellPos += cell.nodeSize;
    });

    // 모든 cell 의 widths 가 충분한 정보를 갖고 있어야 정규화 가능
    //   (각 cell.widths.length === cell.colspan 이고, 모두 양수)
    const allValid = cells.length > 0 && cells.every(
      (c) => c.widths.length === c.colspan && c.widths.every((w) => w > 0)
    );

    if (allValid) {
      // 평탄화된 전체 widths (= 표 전체 폭의 합)
      const flatWidths: number[] = [];
      const cellWidthCount: number[] = []; // 각 cell 의 widths 개수 (= colspan)
      for (const c of cells) {
        for (const w of c.widths) flatWidths.push(w);
        cellWidthCount.push(c.widths.length);
      }
      const rowSum = flatWidths.reduce((a, b) => a + b, 0);

      // overflow 만 잡음 (rowSum > 644+2). underflow (rowSum < 644) 는 rowspan 으로 인한
      // 정상적인 누락이므로 건드리지 않음.
      if (rowSum > PAGE_INNER_PX + 2) {
        const fitted = fitWidthsToPage(flatWidths);
        // fitted 를 다시 cell 별로 분배
        let cursor = 0;
        for (let i = 0; i < cells.length; i++) {
          const cnt = cellWidthCount[i];
          const newW = fitted.slice(cursor, cursor + cnt);
          cursor += cnt;
          if (!arraysEqual(cells[i].widths, newW)) {
            updates.push({ pos: cells[i].pos, newColwidth: newW });
          }
        }
      }
    }

    rowPos += row.nodeSize;
  });

  return updates;
};

export const buildTableNormalizePlugin = () =>
  new Plugin({
    key: tableNormalizePluginKey,
    appendTransaction(transactions, _oldState, newState) {
      if (!transactions.some((tr) => tr.docChanged)) return null;
      // 무한 재귀 방지 — 우리 자신의 tx 면 skip
      if (transactions.some((tr) => tr.getMeta(tableNormalizePluginKey))) return null;

      const updates: Array<{ pos: number; newColwidth: number[] }> = [];
      newState.doc.descendants((node, pos) => {
        if (node.type.name !== "table") return;
        updates.push(...collectCellUpdates(node, pos));
        return false; // nested table 은 스킵 (실용상 불필요)
      });

      if (!updates.length) return null;

      const tr = newState.tr;
      // pos 큰 것부터 적용 — 작은 변경이 큰 pos 를 무효화하지 않게
      updates.sort((a, b) => b.pos - a.pos);
      for (const u of updates) {
        const cellNode = tr.doc.nodeAt(u.pos);
        if (!cellNode) continue;
        tr.setNodeMarkup(u.pos, undefined, {
          ...cellNode.attrs,
          colwidth: u.newColwidth,
        });
      }
      tr.setMeta(tableNormalizePluginKey, { source: "auto-fit-page-width" });
      tr.setMeta("addToHistory", false);
      return tr;
    },
  });
